import { Alert, Button, FormControl, Select, Spinner, Stack, Text } from '@chakra-ui/react'
import type { AxiosError } from 'axios'
import type { VinOnlineVehicleDto, WizardTravelAdditionalData } from 'BrokerTsLib/src/dtos'
import { Insurer } from 'BrokerTsLib/src/enums'
//import { Select } from "chakra-react-select"
import { Controller, useFormContext } from 'BrokerTsLib/src/rhf'
import { useEffect, useState } from 'react'

import { FormLabel } from '@/components/FormLabel'
import { InputGroup } from '@/components/InputGroup'
import { FormRow } from '@/containers/forms/FormRow'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { useSelectedQuote } from '@/context/SelectedQuoteContext'
import { useFindVehicle, useGetAllMakers } from '@/hooks/useVehicle'
import { Cloud } from '@/icons'


export const AdditionalVehicleAssistanceInfoForm = () => {
  const { control, getValues, register, setValue, watch, formState: { errors } } = useFormContext<WizardTravelAdditionalData>()
  const { insuranceParams } = useInsuranceParams()
  const isContract = !!insuranceParams?.generalParams.contractNumber
  const { offer } = useSelectedQuote()
  const showVinForVehicle = offer?.insurerId == Insurer.CPP

  const [vinOnlineValid, setVinOnlineValid] = useState(true)

  const { mutateAsync: findVehicle, isPending: isLoadingFindVehicle } = useFindVehicle()

  const { refetch: refetchAllMakers, data: makersData, isLoading: isLoadingMakers, isFetching: isFetchingMakers } = useGetAllMakers({
    enabled: false,
  })

  useEffect(() => {
    if (makersData?.data && !makersData.data.map((x: any) => x.toUpperCase()).includes(maker?.toUpperCase() ?? '')) setValue('vehicleData.make', '')
  }, [makersData])

  const getVehicleIdentificationParams = () => {
    return {
      spz: getValues('vehicleData.registrationMark')
    }
  }

  const setVinOnlineData = (dto: VinOnlineVehicleDto) => {
    setValue('vehicleData.vin', dto.vin)
    setValue('vehicleData.make', dto.MakeBase)
    setValue('vehicleData.model', dto.ModelText)
  }

  const clearLoadedData = () => {
    setValue('vehicleData.vin', '')
    setValue('vehicleData.make', '')
    setValue('vehicleData.model', '')
  }

  const maker = watch('vehicleData.make')

  useEffect(() => { refetchAllMakers() }, [])

  return (
    <>
      <FormRow sx={{ mb: 8 }}>
        <FormControl isInvalid={!!errors.vehicleData?.registrationMark} isRequired>
          <FormLabel >Reg. značka (SPZ)</FormLabel>
          <InputGroup id="spz" htmlSize={10} placeholder="SPZ"
            {...register('vehicleData.registrationMark', {
              onChange: (e) => setValue('vehicleData.registrationMark', e.target.value.toUpperCase())
            })}
            errors={errors.vehicleData?.registrationMark}
            isDisabled={isContract}
          />
        </FormControl>

        <FormControl sx={{ display: 'flex', alignItems: 'center', alignSelf: 'end', py: 0 }} >
          {!isContract &&
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                findVehicle(getVehicleIdentificationParams())
                  .then(response => {
                    if (!response.data.vin || !response.data.MakeBase) {
                      clearLoadedData()
                      setVinOnlineValid(false)
                    }
                    else {
                      setVinOnlineData(response.data)
                      setVinOnlineValid(true)
                    }
                  })
                  .catch((err: AxiosError) => {
                    if (!(err.response && err.response.status === 400 && err.response.data)) return
                    clearLoadedData()
                    setVinOnlineValid(false)
                  })
              }}
              isLoading={isLoadingFindVehicle}
            >
              <Cloud color="secondary.500" />
              Načíst údaje o vozidle
            </Button>
          }
        </FormControl>
      </FormRow>

      {!vinOnlineValid &&
        <Alert colorScheme="orange" mb={8}>
          <Text variant='warning'>
            Nepodařilo se získat informace o vozidle.
          </Text>
        </Alert>
      }

      <FormRow itemsCount={(showVinForVehicle ? 3 : 2)}>
        {showVinForVehicle &&
          <FormControl isInvalid={!!errors.vehicleData?.vin} isRequired>
            <FormLabel>VIN</FormLabel>

            <InputGroup isDisabled={isContract} id="vehicleData.vin" htmlSize={18} maxLength={17}
              {...register('vehicleData.vin', {
                onChange: (e) => setValue('vehicleData.vin', e.target.value.toUpperCase())
              })}
              errors={errors.vehicleData?.vin}
            />
          </FormControl>
        }
        <FormControl isInvalid={!!errors.vehicleData?.make} isRequired>
          <Stack direction={'row'}>
            <FormLabel>Značka</FormLabel>
            {(isLoadingMakers || isFetchingMakers) && <Spinner />}
          </Stack>

          {(makersData?.data.length ?? 0) > 0 &&
            <Controller
              name='vehicleData.make'
              control={control}
              render={({ field: { onChange } }) => (
                <Select id="vehicle.znacka" onChange={(e) => onChange(e.target.value)} isDisabled={isContract} value={maker?.toUpperCase()}>
                  <option value=''>Vyberte značku vozidla</option>
                  {makersData!.data.map((item) => (
                    <option value={item.toUpperCase()} key={item}>{item.toUpperCase()}</option>
                  ))}
                </Select>
              )}
            />
          }

        </FormControl>

        <FormControl isInvalid={!!errors.vehicleData?.model} isRequired>
          <FormLabel>Model</FormLabel>

          <InputGroup isDisabled={isContract} id="vehicleData.model" htmlSize={30}
            {...register('vehicleData.model')}
            errors={errors.vehicleData?.model}
          />
        </FormControl>
      </FormRow>
    </>
  )
}